import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { useNavigate } from 'react-router-dom';
import { monitoringAPI } from '../services/api';

const HOMER_BASE = 'http://sipcapture.sandbox-sngl.com:9080';

function buildHomerUrl(sipCallId, startEpoch, endEpoch) {
  if (!sipCallId) return null;
  const from = ((startEpoch || endEpoch) - 300) * 1000;
  const to = ((endEpoch || startEpoch) + 300) * 1000;
  const query = JSON.stringify({
    timestamp: { from, to },
    param: {
      search: {
        '1_call': { id: '', callid: [sipCallId], uuid: [] },
      },
      location: {},
      transaction: { call: true, registration: false, rest: false },
      id: {},
      timezone: { value: -180, name: 'Local' },
    },
  });
  return `${HOMER_BASE}/search/result?${query}=`;
}

function extractAcdCallId(to) {
  if (!to) return null;
  const m = to.match(/^\*\*(\d+)\*/);
  return m ? m[1] : null;
}

export default function CallsListTable({ cluster, agentId, hours = 24 }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (cluster && agentId) {
      fetchCalls();
    }
  }, [cluster, agentId, hours]);

  const fetchCalls = async () => {
    setLoading(true);
    try {
      const response = await monitoringAPI.get(`/dashboard/audio-quality/agent/${cluster}/${agentId}/calls?hours=${hours}&limit=20`);
      setCalls(response.data.calls || []);
    } catch (err) {
      console.error('Error fetching calls:', err);
    } finally {
      setLoading(false);
    }
  };

  const getMOSColor = (mos) => {
    if (!mos) return 'default';
    if (mos >= 4.0) return 'success';
    if (mos >= 3.5) return 'info';
    if (mos >= 3.0) return 'warning';
    return 'error';
  };

  const getPacketLossColor = (loss) => {
    if (loss === null || loss === undefined) return null;
    if (loss < 1) return 'success';
    if (loss < 3) return 'warning';
    if (loss < 5) return 'orange';
    return 'error';
  };

  const getMOSQualityText = (mos) => {
    if (!mos) return 'No data available';
    if (mos >= 4.0) return 'Good quality, most users satisfied';
    if (mos >= 3.5) return 'Fair, noticeable degradation but calls are comfortable enough for most users';
    if (mos >= 3.0) return 'Users start finding the call quality annoying or difficult';
    return 'Generally considered poor/unacceptable';
  };

  const formatTimestamp = (epoch) => {
    if (!epoch) return 'N/A';
    return new Date(epoch * 1000).toLocaleString();
  };

  const handleCallClick = (callId) => {
    navigate(`/call-detail/${callId}`);
  };

  const handleTraceClick = (e, callId) => {
    e.stopPropagation();
    navigate(`/cdr/${callId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (calls.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        No calls found for this agent in the selected time window
      </Typography>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell align="center">Duration</TableCell>
              <TableCell align="center">MOS</TableCell>
              <TableCell align="center">Jitter</TableCell>
              <TableCell align="center">Loss%</TableCell>
              <TableCell align="center">Quality</TableCell>
              <TableCell>Release</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {calls.map((call) => {
              const homerUrl = buildHomerUrl(call.call_id, call.start_time, call.end_time);
              return (
                <TableRow
                  key={call.call_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleCallClick(call.call_id)}
                >
                  <TableCell>{formatTimestamp(call.end_time)}</TableCell>
                  <TableCell>{call.from || 'N/A'}</TableCell>
                  <TableCell>
                    {(() => {
                      const acdId = extractAcdCallId(call.to);
                      if (acdId && cluster) {
                        return (
                          <a
                            href={`https://${cluster}.singlecomm.com/supervisor/call.sc?id=${acdId}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: '#90caf9', textDecoration: 'none', fontSize: '0.82rem' }}
                          >
                            {call.to}
                          </a>
                        );
                      }
                      return call.to || 'N/A';
                    })()}
                  </TableCell>
                  <TableCell align="center">{call.duration || 0}s</TableCell>
                  <TableCell align="center">
                    {call.mos ? (
                      <Tooltip title={getMOSQualityText(call.mos)} arrow>
                        <Chip
                          label={call.mos.toFixed(2)}
                          size="small"
                          color={getMOSColor(call.mos)}
                        />
                      </Tooltip>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    {call.jitter ? `${call.jitter.toFixed(1)}ms` : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    {call.packet_loss !== null ? (() => {
                      const color = getPacketLossColor(call.packet_loss);
                      if (color === 'orange') {
                        return (
                          <Chip
                            label={`${call.packet_loss.toFixed(2)}%`}
                            size="small"
                            sx={{ backgroundColor: '#ed6c02', color: '#fff' }}
                          />
                        );
                      }
                      return color ? (
                        <Chip
                          label={`${call.packet_loss.toFixed(2)}%`}
                          size="small"
                          color={color}
                        />
                      ) : `${call.packet_loss.toFixed(2)}%`;
                    })() : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    {call.quality_pct ? `${call.quality_pct.toFixed(1)}%` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip label={call.hangup_cause || 'UNKNOWN'} size="small" />
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="RTCP Detail">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCallClick(call.call_id);
                        }}
                      >
                        <TimelineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="CDR">
                      <IconButton
                        size="small"
                        onClick={(e) => handleTraceClick(e, call.call_id)}
                      >
                        <ManageSearchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {homerUrl && (
                      <Tooltip title="SIP Trace in Homer">
                        <IconButton
                          size="small"
                          component="a"
                          href={homerUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <TravelExploreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
